import React, { useEffect, useState, ReactNode } from 'react';
import PropertyDetail from '../Properties/PropertyDetail';
import './HousingPro.css'; // Make sure styles exist or are included

// Error Boundary Component
class ErrorBoundary extends React.Component<{children: ReactNode, onError: (msg: string) => void}, {hasError: boolean}> {
    constructor(props: {children: ReactNode, onError: (msg: string) => void}) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        console.error('[PropertyDetailDrawer] Error:', error);
        this.props.onError(error.message || 'Failed to load property details');
    }

    render() {
        if (this.state.hasError) {
            return null; // Error display is handled in parent
        }
        return this.props.children;
    }
}

interface PropertyDetailDrawerProps {
    propertyId: string;
    onClose: () => void;
}

const PropertyDetailDrawer: React.FC<PropertyDetailDrawerProps> = ({ propertyId, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Simple trick to slide in after mount
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Wait for transition to finish
    };

    return (
        <div className={`fixed inset-0 z-[100] transition-opacity duration-300 ${isVisible ? 'bg-black/30 backdrop-blur-sm opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute right-0 top-0 bottom-0 w-full md:w-[800px] bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Header for drawer */}
                <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 flex-none sticky top-0 z-10">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <i className="fas fa-building text-emerald-600"></i>
                        Property Inspection
                    </h3>
                    <button 
                        onClick={handleClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500 transition-colors"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                
                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto w-full custom-scrollbar">
                    {error ? (
                        <div className="p-8 text-center text-red-600">
                            <p className="font-bold mb-2">Error Loading Property</p>
                            <p className="text-sm">{error}</p>
                            <button
                                onClick={() => setError(null)}
                                className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <ErrorBoundary onError={setError}>
                            <PropertyDetail propertyId={propertyId} isDrawer={true} />
                        </ErrorBoundary>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PropertyDetailDrawer;
